import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Routes, Route, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, Settings, LogOut, 
  Plus, Edit, Trash2, Eye, Search, Menu, X,
  TrendingUp, DollarSign, ShoppingBag, Phone, Mail, UserCheck, Tag,
  Activity, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2,
  CreditCard, XCircle, ArrowRight, Filter, Star, MapPin, UploadCloud, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Sheet, SheetContent, SheetTrigger,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import AnalyticsPage from '@/pages/AnalyticsPage';
import AdminPixelsPage from '@/pages/AdminPixelsPage';
import AdminReviewsPage from '@/pages/AdminReviewsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/logo-transparent.png";

// ===================== ADMIN LAYOUT =====================

function AdminLayout({ children }) {
  const { user, loading, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const userFromState = location.state?.user;
  const currentUser = userFromState || user;
  const currentIsAdmin = userFromState?.is_admin || isAdmin;

  useEffect(() => {
    if (!loading && !currentUser) {}
  }, [loading, currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C41E3A]" />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <img src={LOGO_URL} alt="Outlet Premium" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2 font-['Playfair_Display']">
            Área Administrativa
          </h1>
          <p className="text-gray-600 mb-6">Faça login com sua conta administrativa</p>
          <Button onClick={() => navigate('/entrar', { state: { from: { pathname: '/admin' } } })} className="bg-[#C41E3A] hover:bg-[#5a252c] rounded-full px-8" data-testid="admin-login-btn">
            Entrar
          </Button>
          <Link to="/" className="block mt-4 text-sm text-gray-500 hover:text-[#C41E3A]">Voltar para a loja</Link>
        </div>
      </div>
    );
  }

  if (!currentIsAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 font-['Playfair_Display']">Acesso Restrito</h1>
          <p className="text-gray-600 mb-6">Sua conta nao tem permissao de administrador.<br /><span className="text-sm">Logado como: {currentUser.email}</span></p>
          <div className="space-y-3">
            <Button onClick={logout} variant="outline" className="rounded-full px-8">Sair</Button>
            <Link to="/" className="block text-sm text-gray-500 hover:text-[#C41E3A]">Voltar para a loja</Link>
          </div>
        </div>
      </div>
    );
  }

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', href: '/admin' },
    { icon: ShoppingCart, label: 'Pedidos', href: '/admin/pedidos' },
    { icon: Package, label: 'Produtos', href: '/admin/produtos' },
    { icon: Users, label: 'Clientes', href: '/admin/clientes' },
    { icon: Star, label: 'Avaliações', href: '/admin/avaliacoes' },
    { icon: Tag, label: 'Cupons', href: '/admin/cupons' },
    { icon: ShoppingBag, label: 'Compras com Cupom', href: '/admin/compras-cupom' },
    { icon: Activity, label: 'Pixels Facebook', href: '/admin/pixels' },
    { icon: Settings, label: 'Config', href: '/admin/configuracoes' },
  ];

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-6"><img src={LOGO_URL} alt="Outlet Premium" className="h-10" /></div>
      <nav className="flex-1 px-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          return (
            <Link key={item.href} to={item.href}
              className={`admin-nav-item rounded-lg mb-1 ${isActive ? 'active bg-white/10' : ''}`}
              onClick={() => setMobileMenuOpen(false)}>
              <Icon className="h-5 w-5" /><span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4 px-2">
          {currentUser.picture && <img src={currentUser.picture} alt="" className="w-8 h-8 rounded-full" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{currentUser.name}</p>
            <p className="text-xs opacity-70 truncate">{currentUser.email}</p>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10" onClick={logout}>
          <LogOut className="h-4 w-4 mr-2" />Sair
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="hidden md:flex admin-sidebar flex-col"><Sidebar /></aside>
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#1e293b] text-white z-50 px-4 py-3 flex items-center justify-between">
        <img src={LOGO_URL} alt="Outlet Premium" className="h-8" />
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white"><Menu className="h-6 w-6" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0 bg-[#1e293b] text-white border-none"><Sidebar /></SheetContent>
        </Sheet>
      </div>
      <main className="flex-1 md:ml-0 mt-14 md:mt-0 p-4 md:p-8">{children}</main>
    </div>
  );
}

// ===================== DASHBOARD =====================

function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`, { withCredentials: true }),
      axios.get(`${API}/admin/funnel-stats`, { withCredentials: true }).catch(() => ({ data: null }))
    ]).then(([statsRes, funnelRes]) => {
      setStats(statsRes.data);
      setFunnel(funnelRes.data);
    }).catch(e => console.error('Stats error:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse text-gray-500">Carregando...</div>;

  const statCards = [
    { icon: DollarSign, label: 'Receita', value: `R$ ${(stats?.total_revenue || 0).toFixed(2).replace('.', ',')}`, color: 'bg-emerald-600' },
    { icon: ShoppingCart, label: 'Pedidos', value: stats?.total_orders || 0, color: 'bg-blue-600' },
    { icon: Users, label: 'Clientes', value: stats?.total_customers || 0, color: 'bg-violet-600' },
    { icon: Package, label: 'Produtos', value: stats?.total_products || 0, color: 'bg-amber-600' },
    { icon: AlertTriangle, label: 'Carrinhos Abandonados', value: stats?.abandoned_carts || 0, color: 'bg-red-600' },
    { icon: Eye, label: 'Visitantes', value: stats?.total_visitors || 0, color: 'bg-cyan-600' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className={`w-9 h-9 ${s.color} rounded-lg flex items-center justify-center mb-2`}>
                <Icon className="h-4 w-4 text-white" />
              </div>
              <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
              <p className="text-lg font-bold text-gray-900">{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Funnel Stats */}
      {funnel && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-100" data-testid="funnel-stats">
          <h2 className="text-base font-semibold mb-4 text-gray-800">Funil de Conversao</h2>
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-0">
            {[
              { label: 'Sessoes', value: funnel.total_sessions, color: 'bg-gray-200 text-gray-800' },
              { label: 'Carrinho', value: funnel.added_to_cart, color: 'bg-blue-100 text-blue-800' },
              { label: 'Checkout', value: funnel.started_checkout, color: 'bg-amber-100 text-amber-800' },
              { label: 'Pagamento', value: funnel.started_payment, color: 'bg-violet-100 text-violet-800' },
              { label: 'Finalizado', value: funnel.completed_payment, color: 'bg-emerald-100 text-emerald-800' },
            ].map((step, i, arr) => (
              <div key={step.label} className="flex items-center gap-2 w-full md:w-auto">
                <div className={`${step.color} rounded-lg px-4 py-3 text-center flex-1 md:flex-none md:min-w-[120px]`}>
                  <p className="text-xs font-medium opacity-80">{step.label}</p>
                  <p className="text-xl font-bold">{step.value}</p>
                </div>
                {i < arr.length - 1 && <ArrowRight className="h-4 w-4 text-gray-300 hidden md:block shrink-0" />}
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-4 text-xs text-gray-500">
            <span>Carrinho → Checkout: <strong className="text-gray-700">{funnel.cart_to_checkout_rate}%</strong></span>
            <span>Checkout → Pago: <strong className="text-gray-700">{funnel.checkout_to_payment_rate}%</strong></span>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
        <h2 className="text-base font-semibold mb-4 text-gray-800">Pedidos Recentes</h2>
        {stats?.recent_orders?.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pedido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_orders.map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell className="font-mono text-xs">{order.order_id}</TableCell>
                    <TableCell className="text-sm">{order.customer_name}</TableCell>
                    <TableCell className="text-sm font-medium">R$ {(order.total || 0).toFixed(2).replace('.', ',')}</TableCell>
                    <TableCell><StatusBadge status={order.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Nenhum pedido recente</p>
        )}
      </div>
    </div>
  );
}

// ===================== STATUS BADGE HELPER =====================

const ORDER_STATUS_LABEL = {
  pending: 'Pendente',
  pending_payment: 'Aguardando pagamento',
  paid: 'Pago',
  processing: 'Em preparação',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
};

function StatusBadge({ status }) {
  const map = {
    paid: { label: 'Pago', cls: 'bg-emerald-100 text-emerald-800' },
    pending: { label: 'Pendente', cls: 'bg-amber-100 text-amber-800' },
    pending_payment: { label: 'Aguardando', cls: 'bg-amber-100 text-amber-800' },
    processing: { label: 'Em preparação', cls: 'bg-indigo-100 text-indigo-800' },
    shipped: { label: 'Enviado', cls: 'bg-blue-100 text-blue-800' },
    delivered: { label: 'Entregue', cls: 'bg-emerald-100 text-emerald-800' },
    cancelled: { label: 'Cancelado', cls: 'bg-red-100 text-red-800' },
  };
  const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

// ===================== CUSTOMERS PAGE =====================

function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchCustomers = () => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    axios.get(`${API}/admin/customers`, { withCredentials: true, params })
      .then(r => setCustomers(r.data))
      .catch(e => console.error('Customers error:', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = customers.filter(c =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search) ||
    c.cpf?.includes(search)
  );

  const setQuickRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setDateFrom(fmt(from));
    setDateTo(fmt(to));
  };

  const setToday = () => {
    const today = new Date().toISOString().slice(0, 10);
    setDateFrom(today);
    setDateTo(today);
  };

  const clearDates = () => {
    setDateFrom('');
    setDateTo('');
  };

  // Re-fetch automatically when date filters change (debounced lightly via direct call)
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  return (
    <div data-testid="admin-customers">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Clientes</h1>
        <Badge variant="outline" className="text-sm" data-testid="customers-count">{filtered.length} clientes</Badge>
      </div>

      {/* Search */}
      <div className="mb-4 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Buscar por nome, email, telefone ou CPF..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="customer-search" />
        </div>
      </div>

      {/* Date filters */}
      <div className="mb-5 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtrar por data de cadastro</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-gray-600">De</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-[160px] text-sm"
              data-testid="customer-date-from"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-gray-600">Até</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-[160px] text-sm"
              data-testid="customer-date-to"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" variant="outline" size="sm" onClick={setToday} className="text-xs" data-testid="filter-today">Hoje</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setQuickRange(7)} className="text-xs" data-testid="filter-7d">Últimos 7 dias</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setQuickRange(30)} className="text-xs" data-testid="filter-30d">Últimos 30 dias</Button>
            {(dateFrom || dateTo) && (
              <Button type="button" variant="ghost" size="sm" onClick={clearDates} className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="filter-clear">
                <X className="h-3 w-3 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nenhum cliente encontrado</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Pedidos</TableHead>
                  <TableHead>Total Gasto</TableHead>
                  <TableHead>Cadastro</TableHead>
                  <TableHead>Última Atividade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => {
                  const isOpen = expanded === c.user_id;
                  return (
                    <React.Fragment key={c.user_id}>
                      <TableRow
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpanded(isOpen ? null : c.user_id)}
                        data-testid={`customer-row-${c.user_id}`}
                      >
                        <TableCell className="text-gray-400">
                          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-gray-600">{(c.name || 'U')[0].toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-900">{c.name || 'Sem nome'}</p>
                              <p className="text-xs text-gray-500">{c.cpf || '-'}</p>
                              {c.is_admin && <Badge className="text-[10px] bg-red-100 text-red-700 mt-0.5">Admin</Badge>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Mail className="h-3 w-3 text-gray-400" />{c.email}
                            </div>
                            {c.phone && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                <Phone className="h-3 w-3 text-gray-400" />{c.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">{c.order_count}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-emerald-700">
                            R$ {(c.total_spent || 0).toFixed(2).replace('.', ',')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-xs text-gray-600">{c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '-'}</p>
                            <p className="text-[10px] text-gray-400">{c.auth_provider === 'google' ? 'Google' : 'Email'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {c.last_event ? (
                            <div>
                              <EventBadge type={c.last_event} />
                              <p className="text-[10px] text-gray-400 mt-0.5">{c.last_event_at ? new Date(c.last_event_at).toLocaleString('pt-BR') : ''}</p>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded details */}
                      {isOpen && (
                        <TableRow key={`${c.user_id}-details`} className="bg-gray-50 hover:bg-gray-50">
                          <TableCell colSpan={7} className="p-0">
                            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid={`customer-details-${c.user_id}`}>
                              {/* Dados pessoais */}
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                  <UserCheck className="h-3.5 w-3.5" /> Dados pessoais
                                </h4>
                                <dl className="space-y-2 text-xs">
                                  <div className="flex justify-between gap-3"><dt className="text-gray-500">ID:</dt><dd className="font-mono text-gray-700 truncate" title={c.user_id}>{c.user_id?.slice(0, 16)}...</dd></div>
                                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Nome:</dt><dd className="text-gray-900 font-medium text-right">{c.name || '-'}</dd></div>
                                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Email:</dt><dd className="text-gray-900 text-right break-all">{c.email}</dd></div>
                                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Telefone:</dt><dd className="text-gray-900 text-right">{c.phone || '-'}</dd></div>
                                  <div className="flex justify-between gap-3"><dt className="text-gray-500">CPF:</dt><dd className="text-gray-900 text-right">{c.cpf || '-'}</dd></div>
                                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Provedor:</dt><dd className="text-gray-900 text-right capitalize">{c.auth_provider || 'email'}</dd></div>
                                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Admin:</dt><dd className="text-gray-900 text-right">{c.is_admin ? 'Sim' : 'Não'}</dd></div>
                                </dl>
                              </div>

                              {/* Datas */}
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                  <Activity className="h-3.5 w-3.5" /> Histórico
                                </h4>
                                <dl className="space-y-2 text-xs">
                                  <div className="flex flex-col gap-0.5">
                                    <dt className="text-gray-500">Cadastrado em:</dt>
                                    <dd className="text-gray-900">{c.created_at ? new Date(c.created_at).toLocaleString('pt-BR') : '-'}</dd>
                                  </div>
                                  {c.last_login_at && (
                                    <div className="flex flex-col gap-0.5">
                                      <dt className="text-gray-500">Último login:</dt>
                                      <dd className="text-gray-900">{new Date(c.last_login_at).toLocaleString('pt-BR')}</dd>
                                    </div>
                                  )}
                                  {c.updated_at && (
                                    <div className="flex flex-col gap-0.5">
                                      <dt className="text-gray-500">Atualizado em:</dt>
                                      <dd className="text-gray-900">{new Date(c.updated_at).toLocaleString('pt-BR')}</dd>
                                    </div>
                                  )}
                                  <div className="flex flex-col gap-0.5">
                                    <dt className="text-gray-500">Última atividade:</dt>
                                    <dd className="text-gray-900">
                                      {c.last_event_at ? new Date(c.last_event_at).toLocaleString('pt-BR') : 'Sem registro'}
                                      {c.last_event && <span className="block text-[10px] text-gray-500 mt-0.5">Evento: {c.last_event}</span>}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between gap-3 pt-2 border-t border-gray-100">
                                    <dt className="text-gray-500">Pedidos:</dt>
                                    <dd className="text-gray-900 font-semibold">{c.order_count}</dd>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <dt className="text-gray-500">Total gasto:</dt>
                                    <dd className="text-emerald-700 font-semibold">R$ {(c.total_spent || 0).toFixed(2).replace('.', ',')}</dd>
                                  </div>
                                </dl>
                              </div>

                              {/* Endereços */}
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                  <ShoppingBag className="h-3.5 w-3.5" /> Endereços ({c.addresses?.length || 0})
                                </h4>
                                {(!c.addresses || c.addresses.length === 0) ? (
                                  <p className="text-xs text-gray-400 italic">Sem endereços cadastrados</p>
                                ) : (
                                  <div className="space-y-3">
                                    {c.addresses.map((addr, idx) => (
                                      <div key={idx} className="text-xs text-gray-700 border-l-2 border-gray-200 pl-2">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                          <span className="font-semibold text-gray-900">{addr.label || `Endereço ${idx + 1}`}</span>
                                          {addr.is_default && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0">Padrão</Badge>}
                                        </div>
                                        <p>{addr.street}{addr.number ? `, ${addr.number}` : ''}{addr.complement ? ` — ${addr.complement}` : ''}</p>
                                        <p>{addr.neighborhood ? `${addr.neighborhood} • ` : ''}{addr.city || ''}{addr.state ? `/${addr.state}` : ''}</p>
                                        {addr.cep && <p className="text-gray-500">CEP: {addr.cep}</p>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== EVENT BADGE HELPER =====================

function EventBadge({ type }) {
  const map = {
    add_to_cart: { label: 'Add Carrinho', cls: 'bg-blue-100 text-blue-700', icon: ShoppingCart },
    cart_update: { label: 'Carrinho', cls: 'bg-blue-100 text-blue-700', icon: ShoppingCart },
    remove_from_cart: { label: 'Removeu Item', cls: 'bg-gray-100 text-gray-600', icon: XCircle },
    start_checkout: { label: 'Checkout', cls: 'bg-amber-100 text-amber-700', icon: CreditCard },
    payment_started: { label: 'Pagamento', cls: 'bg-violet-100 text-violet-700', icon: CreditCard },
    payment_completed: { label: 'Pagou', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  };
  const e = map[type] || { label: type, cls: 'bg-gray-100 text-gray-600', icon: Activity };
  const Icon = e.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${e.cls}`}>
      <Icon className="h-3 w-3" />{e.label}
    </span>
  );
}

// ===================== JOURNEY PAGE =====================

function JourneyPage() {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/customer-journeys`, { withCredentials: true })
      .then(r => setJourneys(r.data))
      .catch(e => console.error('Journeys error:', e))
      .finally(() => setLoading(false));
  }, []);

  const filtered = journeys.filter(j => filter === 'all' || j.funnel_stage === filter);

  const funnelColors = {
    visitante: 'bg-gray-100 text-gray-700',
    carrinho: 'bg-blue-100 text-blue-700',
    checkout: 'bg-amber-100 text-amber-700',
    pagamento: 'bg-violet-100 text-violet-700',
    compra_finalizada: 'bg-emerald-100 text-emerald-700',
  };

  const funnelLabels = {
    visitante: 'Visitante',
    carrinho: 'Carrinho',
    checkout: 'Checkout',
    pagamento: 'Pagamento',
    compra_finalizada: 'Compra Finalizada',
  };

  return (
    <div data-testid="admin-journey">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Jornada do Cliente</h1>
        <Badge variant="outline" className="text-sm">{filtered.length} sessoes</Badge>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { key: 'all', label: 'Todos' },
          { key: 'carrinho', label: 'No Carrinho' },
          { key: 'checkout', label: 'No Checkout' },
          { key: 'pagamento', label: 'No Pagamento' },
          { key: 'compra_finalizada', label: 'Compraram' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.key ? 'bg-[#1e293b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">Nenhuma jornada encontrada</div>
        ) : (
          filtered.map(j => (
            <div key={j.session_id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`journey-${j.session_id}`}>
              {/* Journey header */}
              <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpanded(expanded === j.session_id ? null : j.session_id)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-gray-600">{(j.user_name || j.user_email || 'A')[0].toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">{j.user_name || j.user_email || 'Anonimo'}</p>
                    {j.user_email && <p className="text-xs text-gray-500 truncate">{j.user_email}</p>}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  {j.cart_count > 0 && (
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-gray-500">{j.cart_count} {j.cart_count === 1 ? 'item' : 'itens'}</p>
                      <p className="text-sm font-semibold text-gray-900">R$ {(j.cart_total || 0).toFixed(2).replace('.', ',')}</p>
                    </div>
                  )}
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${funnelColors[j.funnel_stage] || 'bg-gray-100 text-gray-600'}`}>
                    {funnelLabels[j.funnel_stage] || j.funnel_stage}
                  </span>
                  {expanded === j.session_id ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded details */}
              {expanded === j.session_id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  {/* Cart items */}
                  {j.cart_items.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Itens no Carrinho</p>
                      <div className="space-y-2">
                        {j.cart_items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-100">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.name || item.product_id}</p>
                              <p className="text-xs text-gray-500">
                                {item.size && `Tam: ${item.size}`}
                                {item.color && ` | Cor: ${item.color}`}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">R$ {((item.price || 0) * (item.quantity || 1)).toFixed(2).replace('.', ',')}</p>
                              <p className="text-xs text-gray-500">Qtd: {item.quantity || 1}</p>
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <p className="text-xs font-semibold text-gray-600">Total do Carrinho</p>
                          <p className="text-sm font-bold text-gray-900">R$ {(j.cart_total || 0).toFixed(2).replace('.', ',')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Event timeline */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Linha do Tempo</p>
                    <div className="space-y-2">
                      {j.events.slice(0, 10).map((evt, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                          <div>
                            <EventBadge type={evt.event_type} />
                            <p className="text-[10px] text-gray-400 mt-0.5">{evt.created_at ? new Date(evt.created_at).toLocaleString('pt-BR') : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Session info */}
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-[10px] text-gray-400">
                    <span>Sessao: {j.session_id.slice(0, 20)}...</span>
                    <span>Ultima atividade: {j.last_activity ? new Date(j.last_activity).toLocaleString('pt-BR') : '-'}</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ===================== PRODUCT FORM MODAL =====================

const EMPTY_PRODUCT = {
  name: '', description: '', price: '', original_price: '', category: 'feminino',
  subcategory: '', sizes: '', colors: '', images: '', stock: '', sold_count: '',
  is_featured: false, is_new: false, is_top_seller_week: false,
  image_fit: 'cover', image_object_position: 'center top',
  videos: []
};

// ===== CONFIG: Subcategorias por categoria (alinhado ao menu do site) =====
const SUBCATEGORY_OPTIONS = {
  feminino: [
    { value: 'fitness', label: 'Fitness' },
    { value: 'vestidos', label: 'Vestidos' },
    { value: 'blusas', label: 'Blusas' },
    { value: 'shorts', label: 'Shorts' },
    { value: 'calcas', label: 'Calças' },
    { value: 'country', label: 'Country' },
    { value: 'conjuntos', label: 'Conjuntos' },
    { value: 'macacoes', label: 'Macacões' },
  ],
  masculino: [
    { value: 'camisetas', label: 'Camisetas' },
    { value: 'camisas', label: 'Camisas' },
    { value: 'calcas', label: 'Calças' },
    { value: 'bermudas', label: 'Bermudas' },
    { value: 'country', label: 'Country' },
    { value: 'conjuntos', label: 'Conjuntos' },
  ],
  infantil: [
    { value: 'meninos', label: 'Meninos' },
    { value: 'meninas', label: 'Meninas' },
  ],
  acessorios: [
    { value: 'bolsas', label: 'Bolsas' },
    { value: 'cintos', label: 'Cintos' },
    { value: 'oculos', label: 'Óculos' },
    { value: 'outros', label: 'Outros' },
  ],
};

// ===== Modelos de descrição prontos =====
const DESCRIPTION_TEMPLATES = [
  {
    label: 'Fitness premium',
    text: `Peça fitness de alta performance com modelagem que valoriza o corpo.
- Tecido tecnológico com toque "segunda pele"
- Costura plana que não machuca durante o treino
- Não solta fiapos, não enruga e seca rapidamente
- Sustentação firme para treinos intensos
- Acabamento premium digno de loja de grife`,
  },
  {
    label: 'Vestido/Blusa elegante',
    text: `Peça versátil que sai do trabalho direto para o happy hour com elegância.
- Tecido nobre com caimento impecável
- Modelagem que afina a silhueta
- Forro interno para conforto e privacidade
- Cores que combinam com qualquer estilo
- Acabamento premium em todas as costuras`,
  },
  {
    label: 'Masculino básico',
    text: `Peça curinga do guarda-roupa masculino, perfeita para o dia a dia.
- Algodão premium pré-encolhido (não diminui na lavagem)
- Modelagem moderna que não fica largo nem apertado
- Costura reforçada para durar muitas lavagens
- Cores que não desbotam
- Caimento que combina com jeans, calça social ou bermuda`,
  },
  {
    label: 'Conjunto fitness',
    text: `Conjunto fitness completo com top e short/legging combinando.
- Tecido com compressão moderada que modela sem apertar
- Top com bojo removível para customizar o look
- Cintura alta que segura a barriga
- Não transparece no agachamento (testado!)
- Combinação pronta para academia, pilates ou rua`,
  },
  {
    label: 'Acessório (bolsa/cinto)',
    text: `Acessório premium que eleva qualquer look com sofisticação discreta.
- Material de alta qualidade com acabamento nobre
- Ferragens dourado fosco resistentes
- Costuras reforçadas em pontos de tensão
- Tamanho prático para o dia a dia
- Embalagem original do produto incluída`,
  },
];

function ProductFormModal({ open, onClose, product, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        original_price: product.original_price?.toString() || '',
        category: product.category || 'feminino',
        subcategory: product.subcategory || '',
        sizes: (product.sizes || []).join(', '),
        colors: (product.colors || []).join(', '),
        images: (product.images || []).join('\n'),
        stock: product.stock?.toString() || '0',
        sold_count: product.sold_count?.toString() || '',
        is_featured: product.is_featured || false,
        is_new: product.is_new || false,
        is_top_seller_week: product.is_top_seller_week || false,
        // Preserva o enquadramento aplicado (não resetar ao editar)
        image_fit: product.image_fit || 'cover',
        image_object_position: product.image_object_position || 'center top',
        videos: product.videos || [],
      });
    } else {
      setForm(EMPTY_PRODUCT);
    }
    setShowTemplates(false);
  }, [product]);

  // Convert images string ↔ array for previews
  const imageList = (form.images || '').split('\n').map(s => s.trim()).filter(Boolean);

  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (arr.length === 0) return;
    const oversize = arr.find(f => f.size > 5 * 1024 * 1024);
    if (oversize) { toast.error(`"${oversize.name}" excede 5 MB`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      arr.forEach(f => fd.append('images', f));
      const { data } = await axios.post(`${API}/admin/products/upload-images`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrls = (data.images || []).map(i => i.url);
      setForm(f => ({
        ...f,
        images: [...imageList, ...newUrls].join('\n'),
      }));
      toast.success(`${newUrls.length} imagem(ns) enviada(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    setForm(f => ({ ...f, images: imageList.filter((_, i) => i !== idx).join('\n') }));
  };

  const moveImage = (idx, dir) => {
    const newList = [...imageList];
    const swap = idx + dir;
    if (swap < 0 || swap >= newList.length) return;
    [newList[idx], newList[swap]] = [newList[swap], newList[idx]];
    setForm(f => ({ ...f, images: newList.join('\n') }));
  };

  // 🎬 Vídeos do produto
  const videoList = form.videos || [];
  const videoInputRef = React.useRef(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const uploadVideo = async (files) => {
    const file = Array.from(files).find(f => f.type.startsWith('video/'));
    if (!file) { toast.error('Selecione um arquivo de vídeo (mp4, mov, webm)'); return; }
    if (file.size > 80 * 1024 * 1024) { toast.error(`"${file.name}" excede 80 MB`); return; }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append('video', file);
      const { data } = await axios.post(`${API}/admin/products/upload-video`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, videos: [...(f.videos || []), { url: data.url, poster: imageList[0] || null }] }));
      toast.success('Vídeo enviado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro no upload do vídeo');
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeVideo = (idx) => {
    setForm(f => ({ ...f, videos: (f.videos || []).filter((_, i) => i !== idx) }));
  };

  const applyTemplate = (tpl) => {
    setForm(f => ({ ...f, description: tpl.text }));
    setShowTemplates(false);
    toast.success('Modelo aplicado');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.category) {
      toast.error('Preencha nome, preço e categoria');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        category: form.category,
        subcategory: (form.subcategory || '').trim(),
        sizes: form.sizes ? form.sizes.split(',').map(s => s.trim()).filter(Boolean) : [],
        colors: form.colors ? form.colors.split(',').map(s => s.trim()).filter(Boolean) : [],
        images: imageList,
        stock: parseInt(form.stock) || 0,
        sold_count: form.sold_count !== '' ? parseInt(form.sold_count) : null,
        is_featured: form.is_featured,
        is_new: form.is_new,
        is_top_seller_week: form.is_top_seller_week,
        // Mantém o enquadramento atual para não desconfigurar as fotos ao salvar
        image_fit: form.image_fit || 'cover',
        image_object_position: form.image_object_position || 'center top',
        videos: form.videos || [],
      };

      if (isEdit) {
        await axios.put(`${API}/products/${product.product_id}`, payload, { withCredentials: true });
        toast.success('Produto atualizado!');
      } else {
        await axios.post(`${API}/products`, payload, { withCredentials: true });
        toast.success('Produto criado!');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const subcatOptions = SUBCATEGORY_OPTIONS[form.category] || [];

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl" onClick={e => e.stopPropagation()} data-testid="product-form-modal">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-xl">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Editar Produto' : 'Novo Produto'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Nome *</label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nome do produto" data-testid="product-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Categoria *</label>
              <select
                value={form.category}
                onChange={e => { set('category', e.target.value); set('subcategory', ''); }}
                className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm bg-white" data-testid="product-category-select"
              >
                <option value="feminino">Feminino</option>
                <option value="masculino">Masculino</option>
                <option value="infantil">Infantil</option>
                <option value="acessorios">Acessórios</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Subcategoria</label>
              <select
                value={form.subcategory}
                onChange={e => set('subcategory', e.target.value)}
                className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm bg-white"
                data-testid="product-subcategory-select"
              >
                <option value="">— Selecione —</option>
                {subcatOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Estoque</label>
              <Input type="number" value={form.stock} onChange={e => set('stock', e.target.value)} placeholder="0" data-testid="product-stock-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Vendidos (social proof)</label>
              <Input type="number" min="0" value={form.sold_count} onChange={e => set('sold_count', e.target.value)} placeholder="ex: 147" data-testid="product-sold-count-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Preço (R$) *</label>
              <Input type="number" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} placeholder="0.00" data-testid="product-price-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Preço Original (R$)</label>
              <Input type="number" step="0.01" value={form.original_price} onChange={e => set('original_price', e.target.value)} placeholder="Preço antes do desconto" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Tamanhos (separados por vírgula)</label>
              <Input value={form.sizes} onChange={e => set('sizes', e.target.value)} placeholder="P, M, G, GG" data-testid="product-sizes-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Cores (separadas por vírgula)</label>
              <Input value={form.colors} onChange={e => set('colors', e.target.value)} placeholder="Preto, Branco, Azul" data-testid="product-colors-input" />
            </div>
          </div>

          {/* Descrição com templates */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">Descrição</label>
              <button
                type="button"
                onClick={() => setShowTemplates(s => !s)}
                className="text-xs text-[#C41E3A] hover:underline font-medium"
                data-testid="btn-toggle-templates"
              >
                {showTemplates ? 'Ocultar modelos' : 'Usar modelo pronto'}
              </button>
            </div>
            {showTemplates && (
              <div className="mb-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 bg-gray-50 p-2 rounded-md border border-gray-200" data-testid="template-list">
                {DESCRIPTION_TEMPLATES.map((tpl, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    className="text-left text-xs bg-white border border-gray-200 hover:border-[#C41E3A] hover:bg-red-50 rounded px-2.5 py-1.5 transition-colors"
                    data-testid={`template-${i}`}
                  >
                    <span className="font-semibold text-gray-800">{tpl.label}</span>
                    <span className="block text-gray-500 truncate">{tpl.text.split('\n')[0]}</span>
                  </button>
                ))}
              </div>
            )}
            <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Descrição do produto..." rows={6} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y" data-testid="product-description-input" />
          </div>

          {/* Upload de imagens — drag & drop + file picker */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Imagens do produto</label>
            <div
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                uploadFiles(e.dataTransfer.files);
              }}
              className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                dragActive ? 'border-[#C41E3A] bg-red-50' : 'border-gray-300 bg-gray-50'
              }`}
              data-testid="image-dropzone"
            >
              {uploading ? (
                <div className="py-4 flex flex-col items-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-[#C41E3A]" />
                  <p className="text-xs text-gray-600">Enviando imagens...</p>
                </div>
              ) : (
                <>
                  <UploadCloud className="h-7 w-7 text-gray-400 mx-auto mb-1.5" />
                  <p className="text-sm font-medium text-gray-700">
                    Arraste imagens aqui ou{' '}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-[#C41E3A] hover:underline font-semibold"
                      data-testid="btn-pick-files"
                    >
                      escolha do computador
                    </button>
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">JPG, PNG, WEBP até 5 MB cada • Múltiplas imagens permitidas</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                data-testid="product-image-file-input"
                onChange={(e) => uploadFiles(e.target.files)}
              />
            </div>

            {imageList.length > 0 && (
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="image-grid">
                {imageList.map((url, idx) => (
                  <div key={idx} className="relative group rounded-md overflow-hidden border border-gray-200 bg-gray-50 aspect-square">
                    <img src={url} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 bg-[#C41E3A] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">CAPA</span>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-center gap-1 p-1 opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => moveImage(idx, -1)}
                        disabled={idx === 0}
                        className="bg-white text-gray-700 rounded p-1 hover:bg-gray-100 disabled:opacity-40 text-[10px]"
                        title="Mover para esquerda"
                      >←</button>
                      <button
                        type="button"
                        onClick={() => moveImage(idx, 1)}
                        disabled={idx === imageList.length - 1}
                        className="bg-white text-gray-700 rounded p-1 hover:bg-gray-100 disabled:opacity-40 text-[10px]"
                        title="Mover para direita"
                      >→</button>
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="bg-red-600 hover:bg-red-700 text-white rounded p-1"
                        title="Remover"
                        data-testid={`btn-remove-img-${idx}`}
                      ><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <details className="mt-2">
              <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-700">Avançado: colar URLs manualmente</summary>
              <textarea
                value={form.images}
                onChange={e => set('images', e.target.value)}
                placeholder={"https://exemplo.com/imagem1.jpg\nhttps://exemplo.com/imagem2.jpg"}
                rows={3}
                className="mt-1.5 w-full rounded-md border border-gray-200 px-3 py-2 text-xs resize-none font-mono"
                data-testid="product-images-input"
              />
            </details>
          </div>

          {/* 🎬 Vídeos do produto */}
          <div data-testid="product-videos-section">
            <label className="text-xs font-medium text-gray-600 mb-1 block">Vídeos do produto</label>
            {videoList.length > 0 && (
              <div className="space-y-2 mb-2">
                {videoList.map((v, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5">
                    <video src={v.url} className="w-14 h-14 rounded object-cover bg-black" muted playsInline />
                    <span className="text-[11px] text-gray-600 flex-1 truncate">{v.url.split('/').pop()}</span>
                    <button type="button" onClick={() => removeVideo(idx)} className="text-red-500 hover:text-red-700 text-xs font-medium" data-testid={`remove-video-${idx}`}>Remover</button>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={e => { if (e.target.files?.length) uploadVideo(e.target.files); e.target.value = ''; }}
              data-testid="product-video-file-input"
            />
            <button
              type="button"
              onClick={() => videoInputRef.current?.click()}
              disabled={uploadingVideo}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              data-testid="add-video-btn"
            >
              {uploadingVideo ? 'Enviando vídeo...' : '+ Adicionar vídeo'}
            </button>
            <p className="text-[10px] text-gray-400 mt-1">mp4, mov ou webm — até 80 MB. A 1ª foto do produto vira a capa do vídeo.</p>
          </div>

          <div className="flex items-center gap-6 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} className="rounded" />
              <span className="text-sm text-gray-700">Destaque</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_new} onChange={e => set('is_new', e.target.checked)} className="rounded" />
              <span className="text-sm text-gray-700">Novo</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="form-top-seller-week">
              <input type="checkbox" checked={form.is_top_seller_week} onChange={e => set('is_top_seller_week', e.target.checked)} className="rounded" />
              <span className="text-sm text-gray-700">🔥 Mais vendido na última semana</span>
            </label>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t">
            {isEdit ? (
              <Link
                to={`/admin/avaliacoes?product=${product?.product_id || ''}`}
                onClick={onClose}
                className="text-xs font-medium text-[#C41E3A] hover:underline flex items-center gap-1"
                data-testid="link-manage-reviews"
              >
                <Star className="h-3.5 w-3.5" /> Gerenciar avaliações deste produto →
              </Link>
            ) : (
              <span className="text-[11px] text-gray-500">
                💡 Após salvar, você pode adicionar avaliações pelo menu "Avaliações"
              </span>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancelar</Button>
              <Button type="submit" disabled={saving || uploading} className="bg-[#C41E3A] hover:bg-[#5a252c] rounded-full" data-testid="product-save-btn">
                {saving ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Produto'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===================== PRODUCTS PAGE =====================

function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const loadProducts = () => {
    setLoading(true);
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(e => console.error(e)).finally(() => setLoading(false));
  };

  useEffect(() => { loadProducts(); }, []);

  const handleDelete = async (product) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${product.name}"?`)) return;
    try {
      await axios.delete(`${API}/products/${product.product_id}`, { withCredentials: true });
      toast.success('Produto excluido!');
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir produto');
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setModalOpen(true);
  };

  const handleNew = () => {
    setEditProduct(null);
    setModalOpen(true);
  };

  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div data-testid="admin-products">
      <ProductFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditProduct(null); }}
        product={editProduct}
        onSaved={loadProducts}
      />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Produtos</h1>
        <Button onClick={handleNew} className="bg-[#C41E3A] hover:bg-[#5a252c] rounded-full" data-testid="new-product-btn"><Plus className="h-4 w-4 mr-2" />Novo Produto</Button>
      </div>
      <div className="mb-6 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Buscar produtos..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? <div className="p-8 text-center">Carregando...</div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Preco</TableHead>
                  <TableHead>Estoque</TableHead>
                  <TableHead>Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.product_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {product.images?.[0] ? <img src={product.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center"><Package className="h-5 w-5 text-gray-400" /></div>}
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.product_id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{product.category}</Badge></TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">R$ {product.price.toFixed(2).replace('.', ',')}</p>
                        {product.original_price && <p className="text-xs text-gray-400 line-through">R$ {product.original_price.toFixed(2).replace('.', ',')}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={product.stock > 10 ? 'default' : product.stock > 0 ? 'secondary' : 'destructive'} className="text-xs">
                        {product.stock}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(product)} data-testid={`edit-${product.product_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(product)} data-testid={`delete-${product.product_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== ORDERS PAGE =====================

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOrder, setEditOrder] = useState(null);
  const [form, setForm] = useState({ tracking_code: '', tracking_url: '', carrier: '', status: '' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied] = useState(null);

  const loadOrders = () => {
    setLoading(true);
    axios.get(`${API}/orders`, { withCredentials: true })
      .then(r => setOrders(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadOrders(); }, []);

  const openEdit = (order) => {
    setEditOrder(order);
    setForm({
      tracking_code: order.tracking_code || '',
      tracking_url: order.tracking_url || '',
      carrier: order.carrier || '',
      status: order.status || '',
    });
  };

  const closeEdit = () => { setEditOrder(null); };

  const quickStatusChange = async (order, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${order.order_id}/tracking`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Status alterado para "${ORDER_STATUS_LABEL[newStatus] || newStatus}"`);
      loadOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const saveTracking = async () => {
    if (!editOrder) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/orders/${editOrder.order_id}/tracking`, {
        tracking_code: form.tracking_code || null,
        tracking_url: form.tracking_url || null,
        carrier: form.carrier || null,
        status: form.status || null,
      }, { withCredentials: true });
      toast.success('Pedido atualizado!');
      closeEdit();
      loadOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar pedido');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">Pedidos</h1>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? <div className="p-8 text-center">Carregando...</div> : orders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nenhum pedido encontrado</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Pedido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Itens</TableHead>
                  <TableHead>Cupom</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rastreio</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => {
                  const isOpen = expanded === order.order_id;
                  const addr = order.address_details || {};
                  const addrFull = order.address || '';
                  return (
                    <React.Fragment key={order.order_id}>
                      <TableRow
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpanded(isOpen ? null : order.order_id)}
                        data-testid={`order-row-${order.order_id}`}
                      >
                        <TableCell className="text-gray-400">
                          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{order.order_id}</TableCell>
                        <TableCell>
                          <p className="text-sm font-medium">{order.customer_name}</p>
                          {order.customer_cpf && <p className="text-[10px] text-gray-500">CPF: {order.customer_cpf}</p>}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1 text-xs text-gray-600"><Mail className="h-3 w-3 text-gray-400" />{order.customer_email}</div>
                            {order.customer_phone && <div className="flex items-center gap-1 text-xs text-gray-600"><Phone className="h-3 w-3 text-gray-400" />{order.customer_phone}</div>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{order.items?.length || 0} itens</span>
                        </TableCell>
                        <TableCell>
                          {order.coupon_code ? (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-800" data-testid={`order-coupon-${order.order_id}`}>
                                <Tag className="h-3 w-3" />{order.coupon_code}
                              </span>
                              {order.coupon_discount > 0 && (
                                <p className="text-[10px] text-violet-600 mt-0.5">-R$ {order.coupon_discount.toFixed(2).replace('.', ',')}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Sem cupom</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-medium">R$ {(order.total || 0).toFixed(2).replace('.', ',')}</TableCell>
                        <TableCell><StatusBadge status={order.status} /></TableCell>
                        <TableCell>
                          {order.tracking_code ? (
                            <span className="font-mono text-[10px] text-violet-700">{order.tracking_code}</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">{order.created_at ? new Date(order.created_at).toLocaleDateString('pt-BR') : '-'}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); openEdit(order); }}
                            data-testid={`btn-edit-order-${order.order_id}`}
                          >
                            Rastreio
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Expanded details */}
                      {isOpen && (
                        <TableRow className="bg-gray-50 hover:bg-gray-50">
                          <TableCell colSpan={11} className="p-0">
                            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid={`order-details-${order.order_id}`}>
                              {/* Endereço de entrega */}
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                  <MapPin className="h-3.5 w-3.5" /> Endereço de entrega
                                </h4>
                                {addrFull ? (
                                  <>
                                    <div className="text-xs text-gray-800 space-y-1 mb-3">
                                      <p className="font-medium">{order.customer_name}</p>
                                      {addr.street && (
                                        <p>{addr.street}{addr.number ? `, ${addr.number}` : ''}{addr.complement ? ` — ${addr.complement}` : ''}</p>
                                      )}
                                      {addr.neighborhood && <p>{addr.neighborhood}</p>}
                                      {(addr.city || addr.state) && <p>{addr.city || ''}{addr.state ? ` / ${addr.state}` : ''}</p>}
                                      {addr.cep && <p className="text-gray-500">CEP: <span className="font-mono">{addr.cep}</span></p>}
                                      {order.customer_phone && <p className="text-gray-500 mt-1">Telefone: {order.customer_phone}</p>}
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full text-xs"
                                      onClick={() => copyToClipboard(`${order.customer_name}\n${addrFull}\nTel: ${order.customer_phone || ''}\nCPF: ${order.customer_cpf || ''}`, `addr-${order.order_id}`)}
                                      data-testid={`btn-copy-addr-${order.order_id}`}
                                    >
                                      {copied === `addr-${order.order_id}` ? <><CheckCircle2 className="h-3 w-3 mr-1.5 text-emerald-600" /> Copiado!</> : <><Eye className="h-3 w-3 mr-1.5" /> Copiar endereço completo</>}
                                    </Button>
                                  </>
                                ) : (
                                  <p className="text-xs text-gray-400 italic">Endereço não informado</p>
                                )}
                              </div>

                              {/* Itens */}
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                  <Package className="h-3.5 w-3.5" /> Itens do pedido ({order.items?.length || 0})
                                </h4>
                                <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                                  {(order.items || []).map((it, idx) => (
                                    <div key={idx} className="flex items-start gap-2 text-xs border-b border-gray-100 pb-2 last:border-0">
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900">{it.product_name || it.name || 'Item'}</p>
                                        <p className="text-gray-500 text-[11px]">
                                          {it.size && `Tam: ${it.size}`}{it.size && it.color ? ' • ' : ''}{it.color && `Cor: ${it.color}`}{(it.size || it.color) && ' • '}Qtd: {it.quantity || 1}
                                        </p>
                                      </div>
                                      <p className="font-medium text-gray-800 whitespace-nowrap">
                                        R$ {((it.price || 0) * (it.quantity || 1)).toFixed(2).replace('.', ',')}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                                {(order.items?.length || 0) === 0 && <p className="text-xs text-gray-400 italic">Sem itens</p>}
                              </div>

                              {/* Pagamento + Ações rápidas */}
                              <div className="space-y-3">
                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                    <CreditCard className="h-3.5 w-3.5" /> Pagamento
                                  </h4>
                                  <dl className="space-y-1.5 text-xs">
                                    <div className="flex justify-between gap-2"><dt className="text-gray-500">Método:</dt><dd className="text-gray-900 font-medium">{order.payment_method || '-'}</dd></div>
                                    <div className="flex justify-between gap-2"><dt className="text-gray-500">Subtotal:</dt><dd className="text-gray-900">R$ {(order.subtotal || 0).toFixed(2).replace('.', ',')}</dd></div>
                                    {order.coupon_discount > 0 && (
                                      <div className="flex justify-between gap-2"><dt className="text-violet-600">Cupom:</dt><dd className="text-violet-700">-R$ {order.coupon_discount.toFixed(2).replace('.', ',')}</dd></div>
                                    )}
                                    {order.cashback_used > 0 && (
                                      <div className="flex justify-between gap-2"><dt className="text-emerald-600">Cashback:</dt><dd className="text-emerald-700">-R$ {order.cashback_used.toFixed(2).replace('.', ',')}</dd></div>
                                    )}
                                    <div className="flex justify-between gap-2 pt-1.5 border-t border-gray-100"><dt className="text-gray-500">Total pago:</dt><dd className="text-gray-900 font-bold">R$ {(order.total || 0).toFixed(2).replace('.', ',')}</dd></div>
                                    {order.payment_id && (
                                      <div className="flex justify-between gap-2"><dt className="text-gray-500">ID pag:</dt><dd className="font-mono text-[10px] text-gray-700 truncate" title={order.payment_id}>{order.payment_id}</dd></div>
                                    )}
                                  </dl>
                                </div>

                                {/* Quick status change */}
                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                    <Activity className="h-3.5 w-3.5" /> Atualizar status (rápido)
                                  </h4>
                                  <p className="text-[10px] text-gray-500 mb-2">O cliente vê esta mudança imediatamente na página de rastreio.</p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {[
                                      { v: 'processing', label: 'Em preparação' },
                                      { v: 'shipped', label: 'Enviado' },
                                      { v: 'delivered', label: 'Entregue' },
                                      { v: 'cancelled', label: 'Cancelado' },
                                    ].map(opt => (
                                      <Button
                                        key={opt.v}
                                        size="sm"
                                        variant={order.status === opt.v ? 'default' : 'outline'}
                                        className={`text-[11px] h-7 ${order.status === opt.v ? 'bg-[#C41E3A] hover:bg-[#5a252c]' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); quickStatusChange(order, opt.v); }}
                                        data-testid={`status-${opt.v}-${order.order_id}`}
                                        disabled={order.status === opt.v}
                                      >
                                        {opt.label}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Tracking Edit Modal */}
      <Dialog open={!!editOrder} onOpenChange={(open) => { if (!open) closeEdit(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Atualizar rastreio do pedido</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Status</Label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="form-status"
              >
                <option value="">— Manter atual —</option>
                <option value="pending">Aguardando pagamento</option>
                <option value="paid">Pagamento confirmado</option>
                <option value="processing">Em preparação</option>
                <option value="shipped">Enviado</option>
                <option value="delivered">Entregue</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Transportadora</Label>
              <Input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} placeholder="Ex: Correios, Jadlog, Loggi..." data-testid="form-carrier" />
            </div>
            <div>
              <Label className="text-xs">Código de rastreio</Label>
              <Input value={form.tracking_code} onChange={(e) => setForm({ ...form, tracking_code: e.target.value })} placeholder="Ex: BR123456789BR" data-testid="form-tracking-code" />
            </div>
            <div>
              <Label className="text-xs">Link de rastreio (opcional)</Label>
              <Input value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })} placeholder="https://..." data-testid="form-tracking-url" />
            </div>
            <div className="text-[11px] text-gray-500 bg-blue-50 p-2 rounded">
              Dica: ao preencher o código de rastreio sem alterar o status, o pedido será movido automaticamente para "Enviado".
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} disabled={saving}>Cancelar</Button>
            <Button onClick={saveTracking} disabled={saving} data-testid="btn-save-tracking">
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== COUPONS PAGE =====================

const EMPTY_COUPON = { code: '', discount_percent: '', description: '', first_purchase_only: false, active: true, max_uses: '' };

function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [form, setForm] = useState(EMPTY_COUPON);
  const [saving, setSaving] = useState(false);
  const [expandedCoupon, setExpandedCoupon] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [loadingUsage, setLoadingUsage] = useState(null);

  const loadCoupons = () => {
    setLoading(true);
    axios.get(`${API}/admin/coupons`, { withCredentials: true })
      .then(r => setCoupons(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadCoupons(); }, []);

  const toggleExpand = async (code) => {
    if (expandedCoupon === code) {
      setExpandedCoupon(null);
      return;
    }
    setExpandedCoupon(code);
    if (!usageData[code]) {
      setLoadingUsage(code);
      try {
        const res = await axios.get(`${API}/admin/coupons/${code}/usage`, { withCredentials: true });
        setUsageData(prev => ({ ...prev, [code]: res.data }));
      } catch (e) {
        console.error('Erro ao carregar uso do cupom:', e);
      } finally {
        setLoadingUsage(null);
      }
    }
  };

  const openNew = () => { setEditCoupon(null); setForm(EMPTY_COUPON); setModalOpen(true); };
  const openEdit = (c) => {
    setEditCoupon(c);
    setForm({
      code: c.code, discount_percent: (c.discount_percent * 100).toString(),
      description: c.description, first_purchase_only: c.first_purchase_only,
      active: c.active, max_uses: c.max_uses?.toString() || ''
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discount_percent || !form.description) {
      toast.error('Preencha codigo, desconto e descricao');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        discount_percent: parseFloat(form.discount_percent) / 100,
        description: form.description.trim(),
        first_purchase_only: form.first_purchase_only,
        active: form.active,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null
      };
      if (editCoupon) {
        await axios.put(`${API}/admin/coupons/${editCoupon.code}`, payload, { withCredentials: true });
        toast.success('Cupom atualizado!');
      } else {
        await axios.post(`${API}/admin/coupons`, payload, { withCredentials: true });
        toast.success('Cupom criado!');
      }
      setModalOpen(false);
      loadCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar cupom');
    } finally { setSaving(false); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Excluir cupom "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${code}`, { withCredentials: true });
      toast.success('Cupom excluido!');
      setUsageData(prev => { const n = { ...prev }; delete n[code]; return n; });
      if (expandedCoupon === code) setExpandedCoupon(null);
      loadCoupons();
    } catch (err) { toast.error('Erro ao excluir cupom'); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API}/admin/coupons/${c.code}`, {
        ...c, discount_percent: c.discount_percent, active: !c.active
      }, { withCredentials: true });
      loadCoupons();
      toast.success(c.active ? 'Cupom desativado' : 'Cupom ativado');
    } catch { toast.error('Erro ao atualizar cupom'); }
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Stats gerais
  const totalUses = coupons.reduce((a, c) => a + (c.usage_count || 0), 0);
  const totalRevenue = coupons.reduce((a, c) => a + (c.total_revenue || 0), 0);
  const totalDiscount = coupons.reduce((a, c) => a + (c.total_discount || 0), 0);
  const activeCoupons = coupons.filter(c => c.active).length;

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Cupons</h1>
        <Button onClick={openNew} className="bg-[#C41E3A] hover:bg-[#5a252c] rounded-full" data-testid="new-coupon-btn"><Plus className="h-4 w-4 mr-2" />Novo Cupom</Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Tag, label: 'Cupons Ativos', value: activeCoupons, color: 'bg-violet-600' },
          { icon: Users, label: 'Total de Usos', value: totalUses, color: 'bg-blue-600' },
          { icon: DollarSign, label: 'Receita Gerada', value: `R$ ${totalRevenue.toFixed(2).replace('.', ',')}`, color: 'bg-emerald-600' },
          { icon: TrendingUp, label: 'Desconto Total', value: `R$ ${totalDiscount.toFixed(2).replace('.', ',')}`, color: 'bg-amber-600' },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid={`coupon-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className={`w-9 h-9 ${s.color} rounded-lg flex items-center justify-center mb-2`}>
                <Icon className="h-4 w-4 text-white" />
              </div>
              <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
              <p className="text-lg font-bold text-gray-900">{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Coupon Form Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="coupon-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold">{editCoupon ? 'Editar Cupom' : 'Novo Cupom'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Codigo do Cupom *</label>
                <Input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="EX: PROMO20" disabled={!!editCoupon} data-testid="coupon-code-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Desconto (%) *</label>
                <Input type="number" step="1" min="1" max="100" value={form.discount_percent} onChange={e => set('discount_percent', e.target.value)} placeholder="10" data-testid="coupon-discount-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Descricao *</label>
                <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="10% de desconto na primeira compra" data-testid="coupon-description-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Limite de usos (vazio = ilimitado)</label>
                <Input type="number" min="1" value={form.max_uses} onChange={e => set('max_uses', e.target.value)} placeholder="Ilimitado" />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.first_purchase_only} onChange={e => set('first_purchase_only', e.target.checked)} className="rounded" />
                  <span className="text-sm text-gray-700">Somente primeira compra</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="rounded" />
                  <span className="text-sm text-gray-700">Ativo</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="rounded-full">Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#C41E3A] hover:bg-[#5a252c] rounded-full" data-testid="coupon-save-btn">
                  {saving ? 'Salvando...' : editCoupon ? 'Salvar' : 'Criar Cupom'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Coupons List */}
      <div className="space-y-3">
        {loading ? <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">Carregando...</div> : coupons.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">Nenhum cupom cadastrado</div>
        ) : (
          coupons.map((c) => (
            <div key={c.code} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`coupon-row-${c.code}`}>
              {/* Coupon Header */}
              <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleExpand(c.code)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-11 h-11 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                    <Tag className="h-5 w-5 text-violet-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm text-violet-700">{c.code}</span>
                      <span className="text-sm font-semibold text-gray-900">{(c.discount_percent * 100).toFixed(0)}% OFF</span>
                      {c.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Ativo</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500 text-[10px]">Inativo</Badge>
                      )}
                      {c.first_purchase_only && <Badge className="text-[10px] bg-amber-100 text-amber-700">1a compra</Badge>}
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{c.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  <div className="hidden sm:flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Usos</p>
                      <p className="text-sm font-bold text-gray-900">{c.usage_count || 0}{c.max_uses ? <span className="text-gray-400 font-normal">/{c.max_uses}</span> : ''}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Receita</p>
                      <p className="text-sm font-bold text-emerald-700">R$ {(c.total_revenue || 0).toFixed(2).replace('.', ',')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Desconto</p>
                      <p className="text-sm font-bold text-amber-700">R$ {(c.total_discount || 0).toFixed(2).replace('.', ',')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`edit-coupon-${c.code}`}><Edit className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); toggleActive(c); }} data-testid={`toggle-${c.code}`}>
                      {c.active ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={(e) => { e.stopPropagation(); handleDelete(c.code); }} data-testid={`delete-coupon-${c.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  {expandedCoupon === c.code ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded Usage Details */}
              {expandedCoupon === c.code && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  {/* Mobile stats */}
                  <div className="sm:hidden grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <p className="text-[10px] text-gray-400">Usos</p>
                      <p className="text-base font-bold">{c.usage_count || 0}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <p className="text-[10px] text-gray-400">Receita</p>
                      <p className="text-base font-bold text-emerald-700">R$ {(c.total_revenue || 0).toFixed(0)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <p className="text-[10px] text-gray-400">Desconto</p>
                      <p className="text-base font-bold text-amber-700">R$ {(c.total_discount || 0).toFixed(0)}</p>
                    </div>
                  </div>

                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Historico de Compras com este Cupom</p>

                  {loadingUsage === c.code ? (
                    <div className="text-center py-4 text-gray-500 text-sm">Carregando...</div>
                  ) : usageData[c.code]?.usages?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Pedido</TableHead>
                            <TableHead className="text-xs">Cliente</TableHead>
                            <TableHead className="text-xs">Contato</TableHead>
                            <TableHead className="text-xs">Itens</TableHead>
                            <TableHead className="text-xs">Subtotal</TableHead>
                            <TableHead className="text-xs">Desconto</TableHead>
                            <TableHead className="text-xs">Total Pago</TableHead>
                            <TableHead className="text-xs">Pagamento</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">Data/Hora</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {usageData[c.code].usages.map((u, idx) => (
                            <TableRow key={u.order_id || idx} className="bg-white">
                              <TableCell className="font-mono text-[10px]">{u.order_id || '-'}</TableCell>
                              <TableCell>
                                <p className="text-sm font-medium text-gray-900">{u.customer_name || '-'}</p>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-0.5">
                                  {u.customer_email && <div className="flex items-center gap-1 text-[10px] text-gray-600"><Mail className="h-3 w-3 text-gray-400" />{u.customer_email}</div>}
                                  {u.customer_phone && <div className="flex items-center gap-1 text-[10px] text-gray-600"><Phone className="h-3 w-3 text-gray-400" />{u.customer_phone}</div>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-[180px]">
                                  {u.items?.length > 0 ? u.items.map((item, i) => (
                                    <p key={i} className="text-[10px] text-gray-600 truncate">{item.quantity || 1}x {item.name || item.product_id}</p>
                                  )) : <span className="text-[10px] text-gray-400">-</span>}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs text-gray-600">R$ {(u.subtotal || 0).toFixed(2).replace('.', ',')}</TableCell>
                              <TableCell>
                                <span className="text-xs font-semibold text-red-600">-R$ {(u.coupon_discount || 0).toFixed(2).replace('.', ',')}</span>
                              </TableCell>
                              <TableCell className="text-xs font-bold text-gray-900">R$ {(u.total || 0).toFixed(2).replace('.', ',')}</TableCell>
                              <TableCell>
                                <span className="text-[10px] text-gray-600">{u.payment_method === 'credit_card' ? 'Cartao' : u.payment_method === 'pix' ? 'PIX' : u.payment_method || '-'}</span>
                              </TableCell>
                              <TableCell><StatusBadge status={u.status || 'pending'} /></TableCell>
                              <TableCell>
                                {u.created_at ? (
                                  <div>
                                    <p className="text-xs text-gray-700">{new Date(u.created_at).toLocaleDateString('pt-BR')}</p>
                                    <p className="text-[10px] text-gray-400">{new Date(u.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                                  </div>
                                ) : <span className="text-xs text-gray-400">-</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Summary row */}
                      <div className="flex items-center justify-between p-3 bg-gray-100 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-600">{usageData[c.code].total_uses} compra{usageData[c.code].total_uses !== 1 ? 's' : ''} com este cupom</p>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-gray-500">Receita: <strong className="text-emerald-700">R$ {usageData[c.code].total_revenue.toFixed(2).replace('.', ',')}</strong></span>
                          <span className="text-xs text-gray-500">Desconto: <strong className="text-amber-700">R$ {usageData[c.code].total_discount.toFixed(2).replace('.', ',')}</strong></span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-white rounded-lg border border-gray-100">
                      <Tag className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Nenhuma compra com este cupom ainda</p>
                      <p className="text-xs text-gray-400 mt-1">As compras aparecerão aqui automaticamente</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// ===================== COUPON PURCHASES PAGE =====================

function CouponPurchasesPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCoupon, setExpandedCoupon] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [loadingUsage, setLoadingUsage] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/coupons`, { withCredentials: true })
      .then(r => setCoupons(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = async (code) => {
    if (expandedCoupon === code) { setExpandedCoupon(null); return; }
    setExpandedCoupon(code);
    if (!usageData[code]) {
      setLoadingUsage(code);
      try {
        const res = await axios.get(`${API}/admin/coupons/${code}/usage`, { withCredentials: true });
        setUsageData(prev => ({ ...prev, [code]: res.data }));
      } catch (e) { console.error(e); }
      finally { setLoadingUsage(null); }
    }
  };

  const filtered = coupons.filter(c =>
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalUses = filtered.reduce((a, c) => a + (c.usage_count || 0), 0);
  const totalRevenue = filtered.reduce((a, c) => a + (c.total_revenue || 0), 0);
  const totalDiscount = filtered.reduce((a, c) => a + (c.total_discount || 0), 0);

  return (
    <div data-testid="admin-coupon-purchases">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Compras com Cupom</h1>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Pesquisar cupom (ex: BEMVINDO10, DUDA15...)"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10 rounded-full border-gray-200"
          data-testid="coupon-search-input"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { icon: Tag, label: 'Total de Usos', value: totalUses, color: 'bg-blue-600' },
          { icon: DollarSign, label: 'Receita Gerada', value: `R$ ${totalRevenue.toFixed(2).replace('.', ',')}`, color: 'bg-emerald-600' },
          { icon: TrendingUp, label: 'Desconto Total', value: `R$ ${totalDiscount.toFixed(2).replace('.', ',')}`, color: 'bg-amber-600' },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className={`w-9 h-9 ${s.color} rounded-lg flex items-center justify-center mb-2`}>
                <Icon className="h-4 w-4 text-white" />
              </div>
              <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
              <p className="text-lg font-bold text-gray-900">{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Coupon List */}
      <div className="space-y-3">
        {loading ? <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">Carregando...</div> : filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">
            {searchTerm ? 'Nenhum cupom encontrado para esta pesquisa' : 'Nenhum cupom cadastrado'}
          </div>
        ) : (
          filtered.map((c) => (
            <div key={c.code} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`purchase-row-${c.code}`}>
              {/* Header */}
              <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleExpand(c.code)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-11 h-11 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                    <Tag className="h-5 w-5 text-violet-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm text-violet-700">{c.code}</span>
                      <span className="text-sm font-semibold text-gray-900">{(c.discount_percent * 100).toFixed(0)}% OFF</span>
                      {c.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Ativo</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500 text-[10px]">Inativo</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{c.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  <div className="hidden sm:flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Usos</p>
                      <p className="text-sm font-bold text-gray-900">{c.usage_count || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Receita</p>
                      <p className="text-sm font-bold text-emerald-700">R$ {(c.total_revenue || 0).toFixed(2).replace('.', ',')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Desconto</p>
                      <p className="text-sm font-bold text-amber-700">R$ {(c.total_discount || 0).toFixed(2).replace('.', ',')}</p>
                    </div>
                  </div>
                  {expandedCoupon === c.code ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedCoupon === c.code && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  {/* Mobile stats */}
                  <div className="sm:hidden grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <p className="text-[10px] text-gray-400">Usos</p>
                      <p className="text-base font-bold">{c.usage_count || 0}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <p className="text-[10px] text-gray-400">Receita</p>
                      <p className="text-base font-bold text-emerald-700">R$ {(c.total_revenue || 0).toFixed(0)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <p className="text-[10px] text-gray-400">Desconto</p>
                      <p className="text-base font-bold text-amber-700">R$ {(c.total_discount || 0).toFixed(0)}</p>
                    </div>
                  </div>

                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Historico de Compras</p>

                  {loadingUsage === c.code ? (
                    <div className="text-center py-4 text-gray-500 text-sm">Carregando...</div>
                  ) : usageData[c.code]?.usages?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Pedido</TableHead>
                            <TableHead className="text-xs">Cliente</TableHead>
                            <TableHead className="text-xs">Contato</TableHead>
                            <TableHead className="text-xs">Itens</TableHead>
                            <TableHead className="text-xs">Subtotal</TableHead>
                            <TableHead className="text-xs">Desconto</TableHead>
                            <TableHead className="text-xs">Total Pago</TableHead>
                            <TableHead className="text-xs">Pagamento</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">Data/Hora</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {usageData[c.code].usages.map((u, idx) => (
                            <TableRow key={u.order_id || idx} className="bg-white">
                              <TableCell className="font-mono text-[10px]">{u.order_id || '-'}</TableCell>
                              <TableCell>
                                <p className="text-sm font-medium text-gray-900">{u.customer_name || '-'}</p>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-0.5">
                                  {u.customer_email && <div className="flex items-center gap-1 text-[10px] text-gray-600"><Mail className="h-3 w-3 text-gray-400" />{u.customer_email}</div>}
                                  {u.customer_phone && <div className="flex items-center gap-1 text-[10px] text-gray-600"><Phone className="h-3 w-3 text-gray-400" />{u.customer_phone}</div>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-[180px]">
                                  {u.items?.length > 0 ? u.items.map((item, i) => (
                                    <p key={i} className="text-[10px] text-gray-600 truncate">{item.quantity || 1}x {item.name || item.product_id}</p>
                                  )) : <span className="text-[10px] text-gray-400">-</span>}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs text-gray-600">R$ {(u.subtotal || 0).toFixed(2).replace('.', ',')}</TableCell>
                              <TableCell>
                                <span className="text-xs font-semibold text-red-600">-R$ {(u.coupon_discount || 0).toFixed(2).replace('.', ',')}</span>
                              </TableCell>
                              <TableCell className="text-xs font-bold text-gray-900">R$ {(u.total || 0).toFixed(2).replace('.', ',')}</TableCell>
                              <TableCell>
                                <span className="text-[10px] text-gray-600">{u.payment_method === 'credit_card' ? 'Cartao' : u.payment_method === 'pix' ? 'PIX' : u.payment_method || '-'}</span>
                              </TableCell>
                              <TableCell><StatusBadge status={u.status || 'pending'} /></TableCell>
                              <TableCell>
                                {u.created_at ? (
                                  <div>
                                    <p className="text-xs text-gray-700">{new Date(u.created_at).toLocaleDateString('pt-BR')}</p>
                                    <p className="text-[10px] text-gray-400">{new Date(u.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                                  </div>
                                ) : <span className="text-xs text-gray-400">-</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <div className="flex items-center justify-between p-3 bg-gray-100 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-600">{usageData[c.code].total_uses} compra{usageData[c.code].total_uses !== 1 ? 's' : ''}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-gray-500">Receita: <strong className="text-emerald-700">R$ {usageData[c.code].total_revenue.toFixed(2).replace('.', ',')}</strong></span>
                          <span className="text-xs text-gray-500">Desconto: <strong className="text-amber-700">R$ {usageData[c.code].total_discount.toFixed(2).replace('.', ',')}</strong></span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-white rounded-lg border border-gray-100">
                      <ShoppingBag className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Nenhuma compra com este cupom ainda</p>
                      <p className="text-xs text-gray-400 mt-1">As compras aparecerão aqui automaticamente</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ===================== SETTINGS PAGE =====================

function SettingsPage() {
  return (
    <div data-testid="admin-settings">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">Configuracoes</h1>
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-base font-semibold mb-4">Configuracoes do Site</h2>
        <p className="text-gray-500 text-sm">As configuracoes avancadas serao adicionadas em breve.</p>
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-sm mb-1">Integracao Mercado Pago</h3>
            <p className="text-xs text-gray-500 mb-2">Configure sua conta do Mercado Pago para receber pagamentos.</p>
            <Badge>Ativo</Badge>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-sm mb-1">WhatsApp</h3>
            <p className="text-xs text-gray-500 mb-2">Configure o numero do WhatsApp para atendimento.</p>
            <Badge variant="outline">Em breve</Badge>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== MAIN ADMIN PAGE =====================

export default function AdminPage() {
  return (
    <AdminLayout>
      <Routes>
        <Route index element={<AnalyticsPage />} />
        <Route path="pedidos" element={<OrdersPage />} />
        <Route path="produtos" element={<AdminProductsPage />} />
        <Route path="clientes" element={<CustomersPage />} />
        <Route path="avaliacoes" element={<AdminReviewsPage />} />
        <Route path="cupons" element={<CouponsPage />} />
        <Route path="compras-cupom" element={<CouponPurchasesPage />} />
        <Route path="pixels" element={<AdminPixelsPage />} />
        <Route path="configuracoes" element={<SettingsPage />} />
      </Routes>
    </AdminLayout>
  );
}
